import { type GivingBase, type Pagination, type PaginationData, createGivingBase } from 'models';
import { type InfiniteData, type QueryKey, type UseInfiniteQueryOptions, useInfiniteQuery } from 'query';
import type { ApiClientResponseError, DefaultResponseErrorDataObject } from 'common/services';
import { getGivings } from '../services';
import type { GivingsListFilter } from '../types';
import { getGivingsInfiniteQueryKey } from './keys';

type P = {
  filter?: GivingsListFilter;
  options?: UseInfiniteQueryOptions<
    PaginationData<GivingBase>,
    ApiClientResponseError<DefaultResponseErrorDataObject>,
    InfiniteData<PaginationData<GivingBase>>,
    QueryKey,
    string | undefined
  >;
  pagination: Omit<Pagination, 'position'>;
};

export const useGivingsInfiniteQuery = ({ filter, options, pagination }: P) =>
  useInfiniteQuery<
    PaginationData<GivingBase>,
    string | undefined,
    ApiClientResponseError<DefaultResponseErrorDataObject>
  >({
    async queryFn({ pageParam: cursor }) {
      const response = await getGivings({
        config: {
          params: {
            cursor,
            featured: filter?.featured,
            give_now: filter?.giveNow,
            giving_type: filter?.type,
            interval: filter?.interval,
            limit: pagination.pageSize,
            organization_id: filter?.organizationId,
            scope: filter?.scope,
            search: filter?.search,
          },
        },
      });

      return {
        data: response.data.data.map((giving) => createGivingBase(giving)),
        total: response.data.total,
      };
    },
    initialPageParam: undefined,
    getNextPageParam(lastPage, allPages) {
      if (allPages.length * pagination.pageSize >= lastPage.total) {
        return undefined;
      }

      return lastPage.data.at(-1)?.id;
    },
    queryKey: getGivingsInfiniteQueryKey({ filter, pagination }),
    ...options,
  });
